import i18n, { supportedLanguages, fallbackLocales } from "@/i18n";

// Static imports for all app translation modules
// This is required because Rspack doesn't support dynamic imports with aliases
import sharedMessages from "@/i18n/shared/messages";
import adminConsoleMessages from "@/apps/admin-console/translations/messages";
import coreAppOnboardingMessages from "@/apps/core-app-onboarding/translations/messages";
import creditLineMessages from "@/apps/credit-line/translations/messages";
import dashboardMessages from "@/apps/dashboard/translations/messages";
import loginMessages from "@/apps/person-registration/login/translations/messages";
import merchant3Messages from "@/apps/merchant3/translations/messages";
import personRegistrationMessages from "@/apps/person-registration/translations/messages";

// Translation module registry
const translationRegistry: Record<string, Record<string, unknown>> = {
  shared: sharedMessages,
  "admin-console": adminConsoleMessages,
  "core-app-onboarding": coreAppOnboardingMessages,
  "credit-line": creditLineMessages,
  dashboard: dashboardMessages,
  login: loginMessages,
  merchant3: merchant3Messages,
  "person-registration": personRegistrationMessages,
};

const loadedApps = new Set<string>();

const validateLocale = (locale: string): string => {
  const supportedLocales = supportedLanguages.data.locales.map(
    (lang) => lang.locale
  );
  if (supportedLocales.includes(locale)) {
    return locale;
  }

  if (Object.prototype.hasOwnProperty.call(fallbackLocales, locale)) {
    return locale;
  }

  console.warn(
    `Locale ${locale} is not supported. Falling back to default locale.`
  );
  return "es_MX";
};

export const loadAppTranslations = async (appName: string): Promise<void> => {
  const currentLocale = validateLocale(i18n.global.locale.value);

  // Get fallback locales for current locale
  const fallbacks =
    fallbackLocales[currentLocale as keyof typeof fallbackLocales] || [];
  const allLocales = [currentLocale, ...fallbacks];

  for (const locale of allLocales) {
    const cacheKey = `${appName}-${locale}`;

    // Skip if loaded
    if (loadedApps.has(cacheKey)) {
      continue;
    }

    try {
      console.log(`Loading ${appName} translations for ${locale}...`);

      // Get translations from the static registry
      const messageModule = translationRegistry[appName];
      if (!messageModule) {
        console.error(`Translation module not found for app: ${appName}`);
        continue;
      }

      const translations = messageModule[locale] || {};

      // Convert kebab-case to SCREAMING_SNAKE for namespace
      const namespace =
        appName === "shared"
          ? "SHARED"
          : appName.toUpperCase().replace(/-/g, "_");

      // Get existing messages and add app translations
      const currentMessages = i18n.global.getLocaleMessage(locale) || {};
      const newMessages = {
        ...currentMessages,
        [namespace]: translations,
      };

      // Set the updated messages
      i18n.global.setLocaleMessage(locale, newMessages);
      loadedApps.add(cacheKey);
      console.log(`Loaded ${appName} translations for ${locale}`);
    } catch (error) {
      console.error(
        `Failed to load ${appName} translations for ${locale}:`,
        error
      );
      loadedApps.add(cacheKey);
    }
  }
};

export const loadMultipleAppTranslations = async (
  appNames: string[]
): Promise<void> => {
  const loadPromises = appNames.map((appName) => loadAppTranslations(appName));
  await Promise.allSettled(loadPromises);
};

export const preloadCommonTranslations = async (): Promise<void> => {
  await loadAppTranslations("shared");
};

export { supportedLanguages };
